import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { QuoteActionFieldsFragment, QuoteStatus } from "@/gql/graphql";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SendQuoteDialog } from "@/app/Quotes/Components/Dialogs/SendQuoteDialog";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { PDFIcon } from "@/assets/Icons";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { EnvelopeIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createQuotePdfMutation,
  invoiceQuoteMutation,
  issueQuoteMutation,
} from "@/app/Quotes/GraphQL/quoteMutations";
import { DeclineQuoteDialog } from "@/app/Quotes/Components/Dialogs/DeclineQuoteDialog";
import { AcceptQuoteDialog } from "@/app/Quotes/Components/Dialogs/AcceptQuoteDialog";
import { DollarSignIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

export function useQuoteActions(): ActionsHook<QuoteActionFieldsFragment> {
  const { open } = useDialog(SendQuoteDialog);
  const { mutateAsync } = useGqlMutation(createQuotePdfMutation);
  const { mutateAsync: issueQuote } = useGqlMutation(issueQuoteMutation);
  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const { open: openAcceptDialog } = useDialog(AcceptQuoteDialog);
  const { open: openDeclineDialog } = useDialog(DeclineQuoteDialog);
  const { mutateAsync: invoiceQuote } = useGqlMutation(invoiceQuoteMutation);
  const navigate = useNavigate();
  const t = useTranslations("quote.actions");

  return (quote) => {
    return [
      {
        label: t("actions"),
        items: [
          {
            actions: [
              {
                label: t("accept"),
                Icon: ThumbsUpIcon,
                color: "green",
                isHidden: quote.status !== QuoteStatus.Issued,
                handleAction: () => {
                  openAcceptDialog({ quoteId: quote.id });
                },
              },
              {
                label: t("decline"),
                Icon: ThumbsDownIcon,
                color: "red",
                isHidden: quote.status !== QuoteStatus.Issued,
                handleAction: () => {
                  openDeclineDialog({ quoteId: quote.id });
                },
              },
              {
                label: t("invoice"),
                Icon: DollarSignIcon,
                color: "green",
                isAdminAction: true,
                isHidden: quote.status !== QuoteStatus.Accepted,
                handleAction: async () => {
                  const res = await invoiceQuote({
                    quoteId: quote.id,
                  });

                  toast.success(t("quoteInvoiced"));

                  await navigate({
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id",
                    params: (prev) => ({
                      ...prev,
                      id: res.invoiceQuote.id,
                    }),
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("issue"),
                Icon: PaperAirplaneIcon,
                isAdminAction: true,
                isHidden: quote.status !== QuoteStatus.Draft,
                handleAction: async () => {
                  await issueQuote({
                    id: quote.id,
                  });

                  toast.success(t("quoteIssued"));
                },
              },
              {
                label: t("send"),
                Icon: EnvelopeIcon,
                isHidden: quote.status === QuoteStatus.Draft,
                handleAction: () => {
                  open({
                    quoteId: quote.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("view"),
                Icon: ArrowTopRightOnSquareIcon,
                handleAction: () => {
                  window.open(
                    new URL(
                      `/quotes/${quote.uuid}`,
                      import.meta.env.VITE_APP_URL,
                    ),
                    "_blank",
                  );
                },
              },
            ],
          },

          {
            actions: [
              {
                label: t("pdf"),
                Icon: PDFIcon,
                handleAction: async () => {
                  const url = await mutateAsync({
                    id: quote.id,
                  });
                  openPdf({ url: url.createQuotePdf });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
